import React from "react";
import { type VariantProps } from "class-variance-authority";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TextFieldState = "default" | "focused" | "filled" | "error" | "disabled";

export interface TextFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className" | "style"> {
  state?: TextFieldState;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  leadingBadge?: string;
  style?: React.CSSProperties;
}

// ─── Style maps ───────────────────────────────────────────────────────────────

const TEXT_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
};

function getBoxStyles(state: TextFieldState): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "var(--spacing-m)",
    borderRadius: "var(--radius-s)",
    border: "1px solid transparent",
    boxSizing: "border-box",
    transition: "border-color 120ms ease, background-color 120ms ease",
  };
  switch (state) {
    case "focused":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-brand)" };
    case "filled":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" };
    case "error":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-danger)" };
    case "disabled":
      return { ...base, background: "var(--color-bg-accent)", opacity: 0.6 };
    default:
      return { ...base, background: "var(--color-bg-accent)" };
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TextField({
  state = "default",
  leadingIcon,
  trailingIcon,
  leadingBadge,
  style,
  disabled,
  id,
  ...inputProps
}: TextFieldProps) {
  const resolvedState: TextFieldState = disabled ? "disabled" : state;
  const hasIcons = !!(leadingIcon || trailingIcon);
  const iconColor =
    resolvedState === "error"
      ? "var(--color-content-danger)"
      : "var(--color-content-secondary)";

  const iconStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    width: 16,
    height: 16,
    color: iconColor,
  };

  if (leadingBadge) {
    return (
      <div
        style={{
          ...getBoxStyles(resolvedState),
          padding: 0,
          overflow: "hidden",
          ...style,
        }}
      >
        <span
          style={{
            ...TEXT_STYLE,
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            padding: "0 var(--spacing-m)",
            background: "var(--color-bg-accent)",
            borderRight: "1px solid var(--color-border-primary)",
            color: "var(--color-content-secondary)",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {leadingBadge}
        </span>
        <input
          id={id}
          disabled={disabled}
          {...inputProps}
          style={{
            ...TEXT_STYLE,
            flex: 1,
            minWidth: 0,
            padding: "var(--spacing-m)",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--color-content-primary)",
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
        {trailingIcon && <span style={{ ...iconStyle, marginRight: "var(--spacing-m)" }}>{trailingIcon}</span>}
      </div>
    );
  }

  return (
    <div
      style={{
        ...getBoxStyles(resolvedState),
        gap: hasIcons ? "var(--spacing-s)" : undefined,
        ...style,
      }}
    >
      {leadingIcon && <span style={iconStyle}>{leadingIcon}</span>}

      <input
        id={id}
        disabled={disabled}
        {...inputProps}
        style={{
          ...TEXT_STYLE,
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          outline: "none",
          color: "var(--color-content-primary)",
          cursor: disabled ? "not-allowed" : "text",
        }}
      />

      {trailingIcon && <span style={iconStyle}>{trailingIcon}</span>}
    </div>
  );
}
